// EmailProcessorTool.js
import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { Link } from 'react-router-dom';

import '../src/app.css'; // Adjust path if needed
import logo from './assets/logo3.png';

function EmailProcessorTool() {
  const [logs, setLogs] = useState([]);
  const [files, setFiles] = useState([]);
  const [processedData, setProcessedData] = useState(null);
  const [summary, setSummary] = useState(null);

  // Simple logger
  const addLog = (message) => {
    setLogs((prevLogs) => [...prevLogs, message]);
  };

  // Check if address is missing or placeholder
  const isAddressMissing = (address) => {
    if (!address || address.trim() === '') return true;
    
    // Check for placeholder text (case-insensitive)
    const placeholderPatterns = [
      /\[address to be confirmed in workday prior to start date\]/i,
      /\[not available\]/i,
      /\[to be confirmed\]/i,
      /\[pending\]/i,
      /\[tbd\]/i
    ];
    
    const trimmedAddress = address.trim();
    return placeholderPatterns.some(pattern => pattern.test(trimmedAddress));
  };

  // Handle file selection
  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
    addLog(`${e.target.files.length} file(s) selected.`);
  };

  // Convert HTML to text
  const htmlToTextWithLineBreaks = (htmlString) => {
    let text = htmlString.replace(/<br\s*\/?>/gi, '\n');
    text = text.replace(/<\/(p|div|li|tr|td|h[1-6])>/gi, '\n');

    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'text/html');
    return doc.body.textContent || '';
  };

  // Extract name from filename
  const extractNameFromFilename = (filename) => {
    // Pattern: "Atlassian Welcome Packet For _ Name.html"
    const match = filename.match(/Atlassian Welcome Packet For _ (.+?)\.html$/i);
    if (match) {
      const fullName = match[1].trim();
      const nameParts = fullName.split(/\s+/);
      
      return {
        fullName: fullName,
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '' // Join remaining parts as last name
      };
    }
    return null;
  };

  // Process selected files
  const processFiles = async () => {
    if (files.length === 0) {
      addLog('No files selected.');
      return;
    }
    addLog('Processing files...');

    const emailData = [];

    for (let file of files) {
      try {
        const htmlString = await file.text();
        const rawText = htmlToTextWithLineBreaks(htmlString);
        const lines = rawText.split(/\r?\n/);

        const emailInfo = {};
        for (let line of lines) {
          line = line.trim();
          if (!line) continue;

          const match = line.match(/^(.*?):\s*(.*)$/);
          if (match) {
            let key = match[1].trim();
            let value = match[2].trim();

            if (value.toLowerCase() === '[not available]') {
              value = '';
            }
            // Skip disclaimers with 'CAUTION'
            if (key.toLowerCase().includes('caution')) continue;

            emailInfo[key] = value;
          }
        }

        // Enhanced name field population with filename fallback
        const nameFromFile = extractNameFromFilename(file.name);
        
        // Handle First Name
        if (!emailInfo['First Name'] || emailInfo['First Name'].trim() === '') {
          if (nameFromFile && nameFromFile.firstName) {
            emailInfo['First Name'] = nameFromFile.firstName;
            addLog(`Extracted First Name from filename for ${file.name}: ${nameFromFile.firstName}`);
          }
        }
        
        // Handle Last Name
        if (!emailInfo['Last Name'] || emailInfo['Last Name'].trim() === '') {
          if (nameFromFile && nameFromFile.lastName) {
            emailInfo['Last Name'] = nameFromFile.lastName;
            addLog(`Extracted Last Name from filename for ${file.name}: ${nameFromFile.lastName}`);
          }
        }
        
        // Create PRINT field from First Name (now potentially from filename)
        if (emailInfo['First Name'] && emailInfo['First Name'].trim() !== '') {
          const fn = emailInfo['First Name'].trim();
          emailInfo['PRINT'] = fn.split(/\s+/)[0];
        }

        emailData.push(emailInfo);
        addLog(`Processed file: ${file.name}`);
      } catch (error) {
        addLog(`Error processing file ${file.name}: ${error.message}`);
      }
    }

    addLog(`Extracted data from ${emailData.length} file(s).`);
    if (emailData.length === 0) {
      addLog('No data extracted. Operation cancelled.');
      return;
    }

    // Clean up placeholder addresses (only for Address1)
    emailData.forEach((item) => {
      // Clean up placeholder addresses for Address1 only
      if (item.Address1 && isAddressMissing(item.Address1)) {
        addLog(`Converting placeholder address to empty for ${item['First Name'] || 'unknown'}: ${item.Address1}`);
        item.Address1 = ''; // Convert placeholder to empty string
      }
      
      // For Address2 and Address3, only clean up explicit "[Not Available]" placeholders, not empty values
      ['Address2', 'Address3'].forEach(field => {
        if (item[field] && item[field].toLowerCase() === '[not available]') {
          item[field] = '';
        }
      });
    });

    // Separate valid vs missing using updated logic
    const validData = emailData.filter(
      (item) => !isAddressMissing(item.Address1)
    );
    const missingData = emailData.filter(
      (item) => isAddressMissing(item.Address1)
    );

    addLog(`Valid addresses: ${validData.length}, Missing addresses: ${missingData.length}`);

    // Normalize Country
    validData.forEach((item) => {
      item.Country_norm = item.Country
        ? item.Country.trim().toLowerCase()
        : 'international us';
    });

    // Group by known countries
    const countryCategories = {
      Philippines: 'philippines',
      Australia: 'australia',
      India: 'india',
      'United States of America': 'united states of america'
    };

    const groups = {};
    Object.entries(countryCategories).forEach(([catName, value]) => {
      groups[catName] = validData.filter(
        (row) => row.Country_norm === value
      );
    });

    // Remaining => "International US"
    const matchedValues = Object.values(countryCategories);
    groups['International US'] = validData.filter(
      (row) => !matchedValues.includes(row.Country_norm)
    );

    // Remove temp Country_norm
    Object.keys(groups).forEach((cat) => {
      groups[cat] = groups[cat].map((item) => {
        const newItem = { ...item };
        delete newItem.Country_norm;
        return newItem;
      });
    });

    addLog('Grouping by country completed.');
    const processed = { groups, missingData };
    setProcessedData(processed);

    // Build summary
    const summaryData = Object.entries(groups).map(([groupName, rows]) => ({
      group: groupName,
      count: rows.length,
    }));
    summaryData.push({
      group: 'Missing (no Address1)',
      count: missingData.length,
    });
    setSummary(summaryData);

    addLog('Processing complete. Click "Download ZIP" to save files.');
  };

  // Download single ZIP
  const downloadAllInZip = async () => {
    if (!processedData) {
      addLog('No processed data available.');
      return;
    }

    const { groups, missingData } = processedData;
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const zip = new JSZip();

    // Group files
    for (const [groupName, data] of Object.entries(groups)) {
      if (data.length === 0) {
        addLog(`Skipping ${groupName}: no valid records.`);
        continue;
      }

      const safeName = groupName.replace(/ /g, '_');

      // Collect all keys
      const allKeys = new Set();
      data.forEach((obj) => {
        Object.keys(obj).forEach((key) => allKeys.add(key));
      });
      let headers = [...allKeys];

      // "PRINT" => before "First Name"
      if (headers.includes('First Name') && headers.includes('PRINT')) {
        headers = headers.filter((h) => h !== 'PRINT');
        const idx = headers.indexOf('First Name');
        headers.splice(idx, 0, 'PRINT');
      }

      // XLSX
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data, { header: headers });
      XLSX.utils.book_append_sheet(wb, ws, 'Data');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      zip.file(`${safeName}_${dateStr}.xlsx`, wbout, { binary: true });

      // CSV for PRINT
      const names = data.map((item) => item.PRINT).filter(Boolean);
      if (names.length > 0) {
        const csvContent = 'Name\n' + names.join('\n');
        zip.file(`${safeName}_names_${dateStr}.csv`, csvContent);
      }
    }

    // Missing
    if (missingData.length > 0) {
      const wbMissing = XLSX.utils.book_new();
      const missingKeys = new Set();
      missingData.forEach((obj) => {
        Object.keys(obj).forEach((key) => missingKeys.add(key));
      });
      const missingHeaders = [...missingKeys];

      const wsMissing = XLSX.utils.json_to_sheet(missingData, {
        header: missingHeaders,
      });
      XLSX.utils.book_append_sheet(wbMissing, wsMissing, 'Missing');

      const wbMissingOut = XLSX.write(wbMissing, { bookType: 'xlsx', type: 'array' });
      zip.file(`Combined_Missing_Address_${dateStr}.xlsx`, wbMissingOut, { binary: true });
    }

    try {
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipFileName = `EmailProcessor_${dateStr}.zip`;
      saveAs(zipBlob, zipFileName);
      addLog(`Downloaded ${zipFileName}`);
    } catch (error) {
      addLog(`Error generating ZIP: ${error.message}`);
    }
  };

  return (
    <div className="container">
      
      {/* Your existing content */}
      <img src={logo} alt="Logo" className="page-logo" />

      <h1 className="app-title">Atlassian Orders Processor Tool</h1>

      <div className="file-input-section">
        <label htmlFor="fileInput" className="file-label">
          Select HTML files:
        </label>
        <input
          id="fileInput"
          className="file-input"
          type="file"
          multiple
          accept=".html,.htm"
          onChange={handleFileChange}
        />
      </div>

      <div className="buttons-container">
        <button className="action-button" onClick={processFiles}>
          Run HTML Processing
        </button>
        <button className="action-button" onClick={() => setLogs([])}>
          Clear Log
        </button>
        <button className="action-button" onClick={downloadAllInZip}>
          Download ZIP
        </button>
      </div>

      {summary && (
        <div className="summary-container">
          <h2 className="summary-title">Summary of Processed Data</h2>
          <table className="summary-table">
            <thead>
              <tr>
                <th>Group</th>
                <th># of Records</th>
              </tr>
            </thead>
            <tbody>
              {summary.map(({ group, count }, idx) => (
                <tr key={idx}>
                  <td>{group}</td>
                  <td>{count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="log-container">
        <h2 className="log-title">Log</h2>
        <div className="log-messages">
          {logs.map((log, index) => (
            <div key={index} className="log-entry">
              {log}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default EmailProcessorTool;